import { useEffect, useState, useTransition } from "react";

import { V2Autocomplete } from "@stacklet/ui";
import { Controller, useFormContext } from "react-hook-form";
import { graphql, useRefetchableFragment } from "react-relay";

import {
  createRoleAssignmentChangeHandler,
  toAutocompleteValue,
  type RoleAssignmentComboItem,
} from "app/hooks/forms";
import { createFilterElement } from "app/utils/filters";
import { hasViewPermission } from "app/utils/rbac/permissions";

import type { RepositoryPicker_repositoryConfigs$key } from "./__generated__/RepositoryPicker_repositoryConfigs.graphql";
import type { RepositoryPickerRefetchQuery } from "./__generated__/RepositoryPickerRefetchQuery.graphql";

interface Props {
  /** Query ref for repository configurations */
  queryRef: RepositoryPicker_repositoryConfigs$key;
  /** Whether to allow multiple repository selection */
  multiple?: boolean;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** The name of the field to bind the picker to */
  fieldName?: string;
  /** Label for the picker */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
}

export function RepositoryPicker({
  queryRef,
  multiple = true,
  disabled = false,
  fieldName = "repositoryConfigs",
  label = "Repositories *",
  placeholder = "Repositories *",
}: Props) {
  const [, startTransition] = useTransition();
  const [searchValue, setSearchValue] = useState("");
  const { control } = useFormContext();

  const [data, refetch] = useRefetchableFragment<
    RepositoryPickerRefetchQuery,
    RepositoryPicker_repositoryConfigs$key
  >(
    graphql`
      fragment RepositoryPicker_repositoryConfigs on Query
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 1000 }
        filterElement: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "RepositoryPickerRefetchQuery") {
        repositoryConfigs(first: $first, filterElement: $filterElement) {
          edges {
            node {
              name
              uuid
              roleAssignmentTarget
              userPermissions
            }
          }
        }
      }
    `,
    queryRef,
  );

  const filteredEdges = (data?.repositoryConfigs?.edges ?? []).filter((edge) =>
    hasViewPermission(edge.node.userPermissions),
  );

  const options: RoleAssignmentComboItem[] = filteredEdges.map(
    (edge: {
      node: { name: string; uuid: string; roleAssignmentTarget: string };
    }) => ({
      id: edge.node.uuid,
      name: edge.node.name,
      roleAssignmentTarget: edge.node.roleAssignmentTarget,
    }),
  );

  useEffect(() => {
    startTransition(() => {
      const value = searchValue.trim();
      const filterElement = value
        ? createFilterElement([{ name: "name", value }])
        : undefined;

      refetch(
        {
          first: 1000,
          ...(filterElement ? { filterElement } : {}),
        },
        { fetchPolicy: "network-only" },
      );
    });
  }, [refetch, searchValue]);

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <V2Autocomplete
          className="my-xxs"
          inputRef={field.ref}
          isDisabled={disabled}
          items={options}
          label={label}
          menuTrigger="focus"
          multiple={multiple}
          onBlur={field.onBlur}
          onChange={createRoleAssignmentChangeHandler(field.onChange, multiple)}
          onInputChange={setSearchValue}
          placeholder={placeholder}
          tag={(item: RoleAssignmentComboItem) => (
            <V2Autocomplete.Tag id={item.id} textValue={item.name}>
              {item.name}
            </V2Autocomplete.Tag>
          )}
          value={toAutocompleteValue(field.value, multiple)}
        >
          {(item: RoleAssignmentComboItem) => (
            <V2Autocomplete.Option id={item.id} textValue={item.name}>
              <span>{item.name}</span>
            </V2Autocomplete.Option>
          )}
        </V2Autocomplete>
      )}
    />
  );
}
